import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
  FiPlus,
  FiSearch,
  FiUsers,
  FiMail,
  FiPhone,
  FiEdit,
  FiTrash2,
  FiEye,
  FiRefreshCw,
  FiDollarSign,
  FiUserX,
  FiUserCheck,
  FiAlertTriangle,
  FiGlobe,
} from 'react-icons/fi';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cin: '',
    driverLicenseNumber: '',
    licenseExpirationDate: '',
    dateOfBirth: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Maroc',
    },
  });

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [clientsPerPage] = useState(9); // Show 9 clients per page

  // Add blacklist status state
  const [blacklistStatuses, setBlacklistStatuses] = useState({});

  useEffect(() => {
    fetchClients();
  }, []);

  // Check blacklist status for all clients
  useEffect(() => {
    if (clients.length > 0) {
      checkBlacklistStatuses();
    }
  }, [clients]);

  const checkBlacklistStatuses = async () => {
    const statuses = {};

    for (const client of clients) {
      try {
        const response = await api.get(`/api/blacklist/check/${client._id}`);
        statuses[client._id] = response.data;
      } catch (error) {
        console.error(`Error checking blacklist status for client ${client._id}:`, error);
        statuses[client._id] = { isBlacklisted: false };
      }
    }

    setBlacklistStatuses(statuses);
  };

  const fetchClients = async (page = 1) => {
    try {
      const response = await api.get(`/api/clients?page=${page}&limit=${clientsPerPage}`);
      setClients(response.data.data);

      // Update pagination state
      setTotalClients(response.data.count || 0);
      setTotalPages(Math.ceil((response.data.count || 0) / clientsPerPage));
      setCurrentPage(page);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Échec de la récupération des clients');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        // Update existing client
        await api.put(`/api/clients/${editingClient._id}`, formData);
        toast.success('Client mis à jour avec succès');
        setShowAddModal(false);
        setEditingClient(null);
      } else {
        // Add new client
        try {
          await api.post('/api/clients', formData);
          toast.success('Client ajouté avec succès');
          setShowAddModal(false);
        } catch (blacklistError) {
          // Check if it's a blacklist warning
          if (blacklistError.response?.status === 400 && blacklistError.response?.data?.blacklistInfo) {
            const blacklistInfo = blacklistError.response.data.blacklistInfo;
            const warningMessage = `⚠️ ATTENTION: Ce client est blacklisté par l'agence "${blacklistInfo.agencyName}" pour la raison: "${blacklistInfo.reason}". Il est recommandé de ne pas accepter ce client.`;

            if (window.confirm(warningMessage + '\n\nVoulez-vous quand même ajouter ce client ?')) {
              // User confirmed, proceed with client creation
              // Note: This would require backend modification to allow override
              toast.error('Fonctionnalité d\'override non implémentée. Contactez l\'administrateur.');
              return;
            } else {
              // User cancelled
              toast.error('Ajout du client annulé - Client blacklisté');
              return;
            }
          } else {
            // Regular error
            throw blacklistError;
          }
        }
      }

      // Reset form and refresh
      setFormData({
        name: '',
        email: '',
        phone: '',
        cin: '',
        driverLicenseNumber: '',
        licenseExpirationDate: '',
        dateOfBirth: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'Maroc',
        },
      });
      fetchClients(1); // Retour à la première page après création
    } catch (error) {
      console.error('Error saving client:', error);
      if (editingClient) {
        toast.error('Échec de la mise à jour du client');
      } else {
        toast.error('Échec de l\'ajout du client');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      try {
        await api.delete(`/api/clients/${id}`);
        toast.success('Client supprimé avec succès');
        fetchClients(currentPage); // Maintenir la page actuelle après suppression
      } catch (error) {
        toast.error('Échec de la suppression du client');
      }
    }
  };

  const handleAddToBlacklist = async (client) => {
    const reason = prompt('Raison de la mise en liste noire :');
    if (!reason) return;

    try {
      await api.post('/api/blacklist', {
        clientId: client._id,
        reason: reason,
        notes: ''
      });
      toast.success('Client ajouté à la liste noire avec succès');
      fetchClients(currentPage); // Maintenir la page actuelle après ajout à la blacklist
    } catch (error) {
      console.error('Error adding client to blacklist:', error);
      toast.error('Erreur lors de l\'ajout à la liste noire');
    }
  };

  const handleRemoveFromBlacklist = async (client) => {
    if (window.confirm('Êtes-vous sûr de vouloir retirer ce client de la liste noire ?')) {
      try {
        // D'abord, récupérer l'entrée de blacklist
        const blacklistResponse = await api.get('/api/blacklist');
        const blacklistEntry = blacklistResponse.data.data.find(
          entry => entry.client._id === client._id
        );

        if (blacklistEntry) {
          await api.put(`/api/blacklist/${blacklistEntry._id}/remove`);
          toast.success('Client retiré de la liste noire avec succès');
          fetchClients(currentPage); // Maintenir la page actuelle après retrait de la blacklist
        }
      } catch (error) {
        console.error('Error removing client from blacklist:', error);
        toast.error('Erreur lors du retrait de la liste noire');
      }
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      cin: client.cin || '',
      driverLicenseNumber: client.driverLicenseNumber || '',
      licenseExpirationDate: client.licenseExpirationDate ? client.licenseExpirationDate.split('T')[0] : '',
      dateOfBirth: client.dateOfBirth ? client.dateOfBirth.split('T')[0] : '',
      address: {
        street: client.address?.street || '',
        city: client.address?.city || '',
        state: client.address?.state || '',
        zipCode: client.address?.zipCode || '',
        country: client.address?.country || 'Maroc',
      },
    });
    setShowAddModal(true);
  };

  const closeEditModal = () => {
    setShowAddModal(false);
    setEditingClient(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      cin: '',
      driverLicenseNumber: '',
      licenseExpirationDate: '',
      dateOfBirth: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Maroc',
      },
    });
  };

  const handleUpdateClientStats = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/reservations/update-client-stats');
      toast.success(response.data.message);
      fetchClients(currentPage); // Rafraîchir la liste des clients avec la page actuelle
    } catch (error) {
      console.error('Error updating client stats:', error);
      if (error.response?.status === 401) {
        toast.error('Authentification requise. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de mettre à jour les statistiques clients.');
      } else {
        toast.error('Échec de la mise à jour des statistiques clients');
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      cin: '',
      driverLicenseNumber: '',
      licenseExpirationDate: '',
      dateOfBirth: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Maroc',
      },
    });
    setShowAddModal(true);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchClients(newPage);
    }
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.phone.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Clients</h1>
          <p className="text-gray-600 dark:text-gray-400">Gérez votre base de données clients</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full lg:w-auto">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn bg-brand-green hover:bg-green-700 text-white flex items-center gap-2 shadow-md hover:shadow-lg transition-all text-sm"
          >
            <FiPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Ajouter un client</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
          <button
            onClick={handleUpdateClientStats}
            disabled={loading}
            className="btn bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 shadow-sm text-sm"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Mettre à jour les stats</span>
            <span className="sm:hidden">Stats</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher des clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Clients Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClients.map((client) => (
          <div key={client._id} className="card p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-brand-green group relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-2 -mr-2 w-16 h-16 bg-green-50 dark:bg-green-900/30 rounded-full opacity-50 group-hover:scale-125 transition-transform"></div>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {client.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {client.email}
                </p>
              </div>
              {blacklistStatuses[client._id]?.isBlacklisted && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                  Liste noire
                </span>
              )}
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiPhone className="mr-2" />
                {client.phone}
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiUsers className="mr-2" />
                {client.totalRentals} locations
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiMail className="mr-2" />
                {client.totalSpent.toLocaleString()} MAD dépensé
              </div>
            </div>

            {/* Blacklist Warning Section */}
            {blacklistStatuses[client._id]?.isBlacklisted && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                {blacklistStatuses[client._id].blacklistedBy === 'current' ? (
                  <div className="flex items-center text-red-800 dark:text-red-200">
                    <FiUserX className="mr-2 text-red-600" />
                    <span className="text-sm font-medium">
                      Blacklisté par votre agence
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center text-red-800 dark:text-red-200">
                      <FiAlertTriangle className="mr-2 text-red-600" />
                      <span className="text-sm font-medium">
                        ⚠️ ATTENTION: Client blacklisté par d'autres agences
                      </span>
                    </div>
                    {blacklistStatuses[client._id].blacklistedByAgencies?.map((agency, index) => (
                      <div key={index} className="ml-6 text-xs text-red-700 dark:text-red-300">
                        <div className="flex items-center">
                          <FiGlobe className="mr-1" />
                          <span className="font-medium">{agency.agencyName}</span>
                        </div>
                        <div className="ml-4 text-red-600 dark:text-red-400">
                          Raison: {agency.reason}
                        </div>
                        <div className="ml-4 text-red-600 dark:text-red-400">
                          Date: {new Date(agency.blacklistedAt).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between items-center">
              <Link
                to={`/clients/${client._id}`}
                className="btn bg-primary-100 text-primary-700 hover:bg-primary-200 dark:bg-primary-900/40 dark:text-primary-300 dark:hover:bg-primary-900/60 flex items-center text-sm px-3 py-2 rounded-lg transition-colors border border-transparent hover:border-primary-300"
              >
                <FiEye className="mr-1.5 w-4 h-4" />
                Détails
              </Link>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(client)}
                  className="p-2 text-gray-500 hover:text-brand-green hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                  title="Modifier"
                >
                  <FiEdit className="h-4 w-4" />
                </button>
                {!blacklistStatuses[client._id]?.isBlacklisted ? (
                  <button
                    onClick={() => handleAddToBlacklist(client)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Ajouter à la liste noire"
                  >
                    <FiUserX className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => handleRemoveFromBlacklist(client)}
                    className="p-2 text-red-600 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                    title="Retirer de la liste noire"
                  >
                    <FiUserCheck className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(client._id)}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  title="Supprimer"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredClients.length === 0 && (
        <div className="text-center py-12">
          <FiUsers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Aucun client trouvé</p>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {editingClient ? 'Modifier le client' : 'Ajouter un nouveau client'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nom complet
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    C.I.N
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.cin}
                    onChange={(e) => setFormData({ ...formData, cin: e.target.value.toUpperCase() })}
                    className="input"
                    placeholder="Carte d'Identité Nationale"
                    minLength="8"
                    maxLength="12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Permis de conduire
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.driverLicenseNumber}
                    onChange={(e) => setFormData({ ...formData, driverLicenseNumber: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Permis de conduire
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.driverLicenseNumber}
                    onChange={(e) => setFormData({ ...formData, driverLicenseNumber: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Expiration du permis
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.licenseExpirationDate}
                    onChange={(e) => setFormData({ ...formData, licenseExpirationDate: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date de naissance
                </label>
                <input
                  type="date"
                  required
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Adresse
                </label>
                <input
                  type="text"
                  required
                  value={formData.address.street}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value }
                  })}
                  className="input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ville
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value }
                    })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    État/Région
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address.state}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, state: e.target.value }
                    })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Code postal
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address.zipCode}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, zipCode: e.target.value }
                    })}
                    className="input"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => editingClient ? closeEditModal() : setShowAddModal(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn bg-brand-green hover:bg-green-700 text-white shadow-md transition-all">
                  {editingClient ? 'Mettre à jour' : 'Ajouter le client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pagination Component */}
      {totalPages > 1 && (
        <div className="mt-8 flex justify-center">
          <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            {/* Previous Page Button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Précédent
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, index) => {
                const pageNumber = index + 1;
                const isCurrentPage = pageNumber === currentPage;

                // Show first page, last page, current page, and pages around current page
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${isCurrentPage
                        ? 'bg-brand-green text-white shadow-md'
                        : 'bg-white dark:bg-gray-800 text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return (
                    <span key={pageNumber} className="px-2 py-2 text-gray-400 dark:text-gray-500">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            {/* Next Page Button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      {/* Pagination Info */}
      {totalClients > 0 && (
        <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          Affichage de {((currentPage - 1) * clientsPerPage) + 1} à {Math.min(currentPage * clientsPerPage, totalClients)}
          sur {totalClients} client{totalClients !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default Clients; 